import { useEffect, useState } from 'react';

/** Viewport width below Tailwind's `md` breakpoint (768px) — our phone cutoff. */
const MOBILE_QUERY = '(max-width: 767px)';

function readMatches(): boolean {
  // jsdom (tests) and any SSR context have no matchMedia — default to desktop so
  // the desktop branch renders and existing table-based tests keep passing.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

/**
 * True when the viewport is narrower than Tailwind's `md` breakpoint.
 *
 * Used to render one layout branch at a time (desktop table vs mobile cards)
 * rather than both-in-the-DOM-hidden-by-CSS. That keeps the accessibility tree
 * clean, avoids duplicate nodes, and means jsdom (where matchMedia is absent)
 * always sees the desktop branch — so tests written against the table are
 * unaffected. The initial value is read synchronously, so there is no
 * desktop→mobile flash on first paint in a real browser.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(readMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(mql.matches);
    onChange(); // sync in case the width changed between first render and effect
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
